import { RefreshCw, Inbox, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { FinanceChamadosWorkflow } from '@/components/Financeiro/FinanceChamadosWorkflow'
import { FinanceSlaBadge } from '@/components/Financeiro/FinanceSlaBadge'
import { FINANCE_AREA_META, type FinanceAreaKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import type { FinanceRequestRow } from '@/services/financeService'
import { cn } from '@/utils/cn'

type Props = {
  requests: FinanceRequestRow[]
  loading: boolean
  error: string | null
  selectedRequestId: string | null
  typeLabels?: Record<string, string>
  onRefresh: () => void
  onSelect: (id: string) => void
}

function formatTypeLabel(type: string, typeLabels?: Record<string, string>) {
  return typeLabels?.[type] ?? type.replace(/_/g, ' ')
}

function formatAreaLabel(area: string) {
  return FINANCE_AREA_META[area as FinanceAreaKey]?.label ?? area
}

export function PortalPjMyRequests({
  requests,
  loading,
  error,
  selectedRequestId,
  typeLabels,
  onRefresh,
  onSelect,
}: Props) {
  return (
    <div className='space-y-4'>
      <FinanceChamadosWorkflow variant='compact' />

      <div className='flex items-center justify-end'>
        <Button
          variant='secondary'
          size='sm'
          type='button'
          onClick={onRefresh}
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </Button>
      </div>

      {error && (
        <div className='rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger'>
          {error}
        </div>
      )}

      {loading ? (
        <div className='flex justify-center py-20'>
          <div className='h-9 w-9 animate-spin rounded-full border-2 border-[#e91e63] border-t-transparent' />
        </div>
      ) : requests.length === 0 ? (
        <div className='rounded-2xl border border-dashed border-white/15 bg-white/[0.04] px-6 py-14 text-center'>
          <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e91e63]/10 text-[#e91e63]'>
            <Inbox size={28} aria-hidden />
          </div>
          <p className='text-base font-semibold text-white'>Nenhum chamado ainda</p>
          <p className='mx-auto mt-2 max-w-sm text-sm leading-relaxed text-white/60'>
            Quando você abrir uma solicitação, ela entra na fila do financeiro e aparece aqui com
            protocolo, status e prazo de resposta.
          </p>
        </div>
      ) : (
        <ul className='space-y-3'>
          {requests.map((r) => (
            <li key={r.id}>
              <button
                type='button'
                onClick={() => onSelect(r.id)}
                className={cn(
                    'w-full rounded-2xl border bg-white/[0.05] p-4 text-left shadow-sm transition-all duration-200 hover:border-[#e91e63]/40 hover:bg-white/[0.07]',
                  selectedRequestId === r.id
                    ? 'border-[#e91e63]/50 ring-2 ring-[#e91e63]/20'
                    : 'border-white/10',
                )}
              >
                <div className='flex flex-wrap items-start justify-between gap-2'>
                  <div className='min-w-0'>
                    <p className='font-mono text-xs text-white/45'>{r.protocol}</p>
                    <p className='mt-1 font-medium text-white'>
                      {formatTypeLabel(r.type, typeLabels)}
                    </p>
                    <p className='mt-1 text-xs text-white/45'>{formatAreaLabel(r.area)}</p>
                  </div>
                  <FinanceSlaBadge slaDueAt={r.slaDueAt} status={r.status} />
                </div>
                <div className='mt-3 flex flex-wrap items-center gap-3 text-xs text-white/55'>
                  <span className='rounded-full bg-[#e91e63]/10 px-2.5 py-1 font-medium text-[#b1124a]'>
                    {formatFinanceStatus(r.status)}
                  </span>
                  <span className='inline-flex items-center gap-1'>
                    <MessageSquare size={12} aria-hidden />
                    Aberto em {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
